import { useRef, useState } from 'react';

import InfiniteScroll from '../../../../../../components/InfiniteScroll/InfiniteScroll';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { SaveButton } from '../../../../../../components/Clickables/Buttons/SaveButton';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { PostsViewer } from '../../../../../../components/PostsViewer/PostsViewer';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { reportMykissClientError } from '@lib/errors/client';
import { getFishingForbidden } from '@domain/fishingWater/getFishingForbidden';
import { getWaterPrivacyStatusFromMetadata } from '@domain/fishingWater/getWaterPrivacyStatusFromMetadata';
import { getEdgesFromConnection } from '@graphql/connections/getEdgesFromConnection';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { usePostViewer } from '@domain/posts/usePostViewer';
import { useUserInfo } from '@domain/user/useUserInfo';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';
import { GetFishingWaterSummary } from '../../../../../../generated/graphql';
import { getFishingWaterSummary } from '@graphql/queries/rutilus/FishingWaters';
import { CATCHES_PER_REQUEST, CATCHES_PER_REQUEST_FREE } from '../../../../constants/constants';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';
import { MapDrawerSignup } from '../../MapDrawerSignup/MapDrawerSignup';

import { FishingWaterViewCatchesList } from './FishingWaterViewCatchesList';
import { FishingWaterViewHeader } from './FishingWaterViewHeader';

interface IProps {
  waterId: string;
}

export const FishingWaterView = ({ waterId }: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const { isLoggedIn } = useUserInfo();
  const { loading, data, fetchMore } = useRutilusQuery(getFishingWaterSummary, {
    variables: {
      externalId: waterId,
      first: isLoggedIn ? CATCHES_PER_REQUEST : CATCHES_PER_REQUEST_FREE,
      after: '',
    },
  });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const postsReference = useRef<HTMLDivElement>(null);
  const {
    currentPostViewerId,
    isPostViewerOpen,
    closePostViewer,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer();

  if ((clientHasRendered && loading) || !clientHasRendered) {
    return (
      <MapDrawerLayout className="col-start-1 row-start-1 h-full">
        <div className="flex h-full flex-col">
          <LoadingSpinner loadingText="Loading water..." />
        </div>
      </MapDrawerLayout>
    );
  }

  if (!data) {
    return (
      <MapDrawerLayout className="col-start-1 row-start-1 h-full">
        <div className="flex h-full flex-col">
          <div className="flex">
            <Heading variant="sm" className="pr-2">
              Not Found
            </Heading>
          </div>
          <MapDrawerNotification title="It looks like something went wrong" />
        </div>
      </MapDrawerLayout>
    );
  }

  const hasNextPage = data.fishingWater.catches.pageInfo.hasNextPage;
  const endCursor = data.fishingWater.catches.pageInfo.endCursor;
  const canLoadMore = !loading && hasNextPage && !isFetchingMore;
  const region =
    firstNonEmpty(data.fishingWater.region?.localName, data.fishingWater.region?.name) || undefined;
  const country = data.fishingWater.country?.id ?? undefined;
  const totalAverageRating = data.fishingWater.allReviews?.totalAverage ?? 0;
  const totalRatingCount = data.fishingWater.allReviews?.totalCount ?? 0;

  const {
    displayName,
    latitude,
    longitude,
    catches,
    catchesCount,
    _id: externalId,
    metadata,
  } = data.fishingWater;

  const fetchNextPage = async (): Promise<unknown> => {
    if (!isLoggedIn) {
      return;
    }
    setIsFetchingMore(true);

    trackEvent({
      name: 'mykiss_load_more',
      properties: {
        page: currentPage,
        manual: true,
        source: 'explore_water_catches',
      },
    });

    return fetchMore({
      context: {},
      variables: {
        after: endCursor,
        first: CATCHES_PER_REQUEST,
      },
      updateQuery: (prev: GetFishingWaterSummary, { fetchMoreResult }): GetFishingWaterSummary => {
        return {
          ...fetchMoreResult,
          fishingWater: {
            ...fetchMoreResult.fishingWater,
            catches: {
              ...fetchMoreResult.fishingWater.catches,
              edges: [
                ...getEdgesFromConnection(prev.fishingWater.catches),
                ...getEdgesFromConnection(fetchMoreResult.fishingWater.catches),
              ],
              pageInfo: fetchMoreResult.fishingWater.catches.pageInfo,
            },
          },
        };
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissClientError);
  };

  const catchPostList = getNodesFromConnection(catches).map(_catch => _catch.post);
  const waterPrivacyStatus = getWaterPrivacyStatusFromMetadata(metadata);
  const fishingForbidden = getFishingForbidden(waterPrivacyStatus);

  return (
    <MapDrawerLayout
      drawerRef={postsReference}
      contentId="waters-list"
      headerContent={
        <FishingWaterViewHeader
          displayName={displayName}
          region={region}
          country={country}
          totalAverageRating={totalAverageRating}
          totalRatingCount={totalRatingCount}
          catchesCount={catchesCount}
          longitude={longitude}
          latitude={latitude}
          externalId={externalId}
          metadata={metadata}
          fishingForbidden={fishingForbidden}
        />
      }
    >
      {!isLoggedIn && (
        <MapDrawerSignup
          headingText="See all catches in the Fishbrain app"
          bodyText="In the app you can see all catches in this area"
          downloadButtonText="Download the app"
        />
      )}
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={catchPostList.length}
        hasMore={canLoadMore}
        scrollableTarget="waters-list"
        loader={<></>}
      >
        <div className="flex h-full flex-col">
          {!fishingForbidden && catchesCount > 0 && (
            <>
              <div>
                <FishingWaterViewCatchesList
                  catchPosts={catchPostList}
                  openPostViewer={openPostViewer}
                />
              </div>

              {hasNextPage && isLoggedIn && (
                <div className="my-8 flex w-full justify-center">
                  <SaveButton
                    onClick={() => {
                      void fetchNextPage();
                    }}
                    variant="buttonPrimary"
                    savingLabel="Loading..."
                    isSaving={loading}
                  >
                    Load more
                  </SaveButton>
                </div>
              )}
            </>
          )}

          {!fishingForbidden && catchesCount === 0 && (
            <MapDrawerNotification title="No catches in this water" withIcon />
          )}

          {fishingForbidden && <MapDrawerNotification title="Fishing is forbidden here" withIcon />}
        </div>
      </InfiniteScroll>
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={catchPostList}
          currentPostViewerId={currentPostViewerId}
          hasMore={canLoadMore}
          isPostViewerOpen
          loadMore={() => {
            void fetchNextPage();
          }}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </MapDrawerLayout>
  );
};
